import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getAccessToken } from '@/utils/jwt';

export interface ScheduleItem {
  id: string;
  type: 'connector_sync' | 'connector' | 'agent_script';
  name: string;
  agent_name?: string;
  /** connector_id for connector-type schedules (maps to PUT /connectors/{id}/schedule). */
  connector_id?: string;
  schedule: string;
  human_readable: string;
  timezone: string;
  enabled: boolean;
  next_fire_at: string | null;
  last_fired_at: string | null;
  run_as_user?: string;
  link: string;
}

export interface SchedulePreset {
  key: string;
  label: string;
  cron: string;
}

export interface ConnectorScheduleResponse {
  id: string;
  connector_id: string;
  cron_schedule: string;
  cron_timezone: string;
  cron_enabled: boolean;
  run_as_user: string | null;
  next_fire_at: string | null;
  human_readable: string;
  next_runs: string[];
}

export interface SetScheduleBody {
  schedule_mode: string;
  preset?: string;
  interval_value?: number;
  interval_unit?: string;
  cron_expression?: string;
  timezone: string;
  run_as_user_id?: string;
}

export interface SetScheduleRequest {
  connectorId: string;
  body: SetScheduleBody;
}

/**
 * Request body for toggling a connector schedule's enabled state.
 * Uses the existing PUT /connectors/{id}/schedule endpoint with the
 * cron_enabled field.
 *
 * TODO: Add a dedicated PATCH /schedules/{id}/toggle endpoint on the
 * backend to support toggling agent_script type schedules that are not
 * tied to a connector.
 */
export interface ToggleConnectorScheduleRequest {
  connectorId: string;
  enabled: boolean;
  cron_schedule: string;
  timezone: string;
}

export interface EditScheduleRequest {
  id: string;
  cron_expression: string;
  timezone: string;
}

export const schedulesApi = createApi({
  reducerPath: 'schedulesApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_AGENT_MCP_URL || '/agent',
    prepareHeaders: (headers) => {
      const token = getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Schedule', 'SchedulePreset'],
  endpoints: (builder) => ({
    listSchedules: builder.query<ScheduleItem[], undefined>({
      query: () => '/schedules',
      transformResponse: (resp: { schedules: ScheduleItem[] }) => resp.schedules,
      providesTags: ['Schedule'],
    }),
    listPresets: builder.query<SchedulePreset[], undefined>({
      query: () => '/schedules/presets',
      transformResponse: (resp: { presets: SchedulePreset[] }) => resp.presets,
      providesTags: ['SchedulePreset'],
    }),
    setConnectorSchedule: builder.mutation<ConnectorScheduleResponse, SetScheduleRequest>({
      query: ({ connectorId, body }) => ({
        url: `/connectors/${connectorId}/schedule`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Schedule'],
    }),
    deleteConnectorSchedule: builder.mutation<Record<string, never>, string>({
      query: (connectorId) => ({
        url: `/connectors/${connectorId}/schedule`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Schedule'],
    }),
    /**
     * Toggle enabled state for a connector-type schedule.
     * Re-uses PUT /connectors/{id}/schedule with schedule_mode=cron and
     * the existing cron expression, flipping the enabled flag.
     *
     * TODO: Backend needs PATCH /schedules/{id}/toggle to support
     * agent_script schedule type toggles without requiring cron params.
     */
    toggleConnectorSchedule: builder.mutation<
      ConnectorScheduleResponse,
      ToggleConnectorScheduleRequest
    >({
      query: ({ connectorId, enabled, cron_schedule, timezone }) => ({
        url: `/connectors/${connectorId}/schedule`,
        method: 'PUT',
        body: {
          schedule_mode: 'cron',
          cron_expression: cron_schedule,
          timezone,
          enabled,
        },
      }),
      invalidatesTags: ['Schedule'],
    }),
  }),
});

export const {
  useListSchedulesQuery,
  useListPresetsQuery,
  useSetConnectorScheduleMutation,
  useDeleteConnectorScheduleMutation,
  useToggleConnectorScheduleMutation,
} = schedulesApi;
